/**
 * Shared display logic for email recipients. Both the collapsed recipients row and the expanded
 * recipients editor render contact display names rather than raw addresses, so they must agree on
 * when the address is folded into the visible label and when it is only available on hover.
 */

/** Minimal shape of a contact, structurally satisfied by `InboxContactFragment`. */
export interface RecipientContact {
  displayName?: string | null;
  email: string;
}

export interface ResolvedRecipient {
  /** The text rendered to the user. */
  label: string;
  /** The full address behind the label. Equal to `label` when the label already is the address. */
  email: string;
}

/**
 * Builds the label shown for a contact. The address is inlined only when another contact shares the
 * exact same display name, which is the pre-existing behavior; near-duplicate names (differing by
 * case or whitespace) fall through to the bare display name and rely on hover disclosure instead.
 */
export const buildRecipientLabel = (contact: RecipientContact, contacts: RecipientContact[]): string => {
  const isDuplicateName =
    !!contact.displayName && contacts.filter((c) => c.displayName === contact.displayName).length > 1;

  return isDuplicateName ? `${contact.displayName} (${contact.email})` : contact.displayName || contact.email;
};

/**
 * Resolves a recipient address against the contact list, returning both the label to render and the
 * full address it stands for. A recipient matching no contact renders as its own raw address.
 */
export const resolveRecipient = (recipient: string, contacts: RecipientContact[]): ResolvedRecipient => {
  const contact = contacts.find((c) => c.email === recipient);

  if (!contact) {
    return { label: recipient, email: recipient };
  }

  return { label: buildRecipientLabel(contact, contacts), email: contact.email };
};

/** Whether the full address is worth disclosing, i.e. the visible label does not already show it. */
export const isRecipientEmailHidden = ({ label, email }: ResolvedRecipient): boolean => label !== email;
